/**
 * KAILAS Size Chart Enhanced Interactions
 * Provides accordion functionality and smooth animations
 * Also handles modal size chart table generation
 */

(function() {
  'use strict';

  function getDataLines(data) {
    if (!data) return [];
    return data
      .replace(/\r/g, '')
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
  }

  function splitDataLine(line) {
    return line
      .split(/[\t,，;；]+/)
      .map(part => part.trim());
  }

  function toNumber(value) {
    const normalized = String(value || '').replace(/[^\d.-]/g, '');
    const num = parseFloat(normalized);
    return Number.isFinite(num) ? num : null;
  }

  function parseRangeValue(value) {
    const text = String(value || '').trim();
    if (!text) return null;

    const parts = text.split('-').map(v => v.trim());
    const min = toNumber(parts[0]);
    if (min === null) return null;

    const max = parts.length > 1 ? toNumber(parts[1]) : min;
    if (max === null) return null;

    return { min, max };
  }

  function formatRange(min, max, digits) {
    const fmt = (n) => {
      const fixed = Number(n).toFixed(digits);
      return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
    };

    if (max === undefined || max === null || min === max) return fmt(min);
    return `${fmt(min)}-${fmt(max)}`;
  }

  function normalizeCategoryLabel(category) {
    const raw = String(category || '').trim();
    const key = raw.toLowerCase().replace(/['’]/g, '');

    if (key.includes('women')) return "Women's";
    if (key.includes('men')) return "Men's";
    if (key.includes('unisex')) return 'Unisex';
    return raw;
  }

  // Initialize modal size chart tables
  function initModalSizeChart(modalElement) {
    // Find the specific modal (or use the one that's currently open)
    let modal = modalElement;
    if (!modal) {
      modal = document.querySelector('.sc-modal-overlay:not([hidden])');
    }
    if (!modal) {
      modal = document.querySelector('.sc-modal-overlay');
    }
    if (!modal) return;
    
    // Find the table wrapper INSIDE this specific modal
    const wrapper = modal.querySelector('.size-chart-table-wrapper');
    if (!wrapper) return;
    
    const chartType = wrapper.getAttribute('data-chart-type');
    const sizeData = wrapper.getAttribute('data-size-chart');
    const tbody = wrapper.querySelector('.size-chart-tbody');
    
    if (!tbody || !sizeData) return;
    
    // Clear existing rows
    tbody.innerHTML = '';
    
    // Parse based on chart type
    if (chartType === 'men_shoes') {
      parseMenShoesData(sizeData, tbody, wrapper);
    } else if (chartType === 'women_shoes') {
      parseWomenShoesData(sizeData, tbody, wrapper);
    } else if (chartType === 'men_shoes_w') {
      parseMenShoesWData(sizeData, tbody, wrapper);
    } else if (chartType === 'gloves') {
      parseGlovesData(sizeData, tbody, wrapper);
    } else if (chartType === 'socks') {
      parseSocksData(sizeData, tbody, wrapper);
    } else if (chartType === 'vest_packs' || chartType === 'waist_packs') {
      parseCircumferenceData(sizeData, tbody, wrapper);
    } else if (chartType === 'tops') {
      parseTopsData(sizeData, tbody, wrapper);
    } else if (chartType === 'bottoms') {
      parseBottomsData(sizeData, tbody, wrapper);
    } else if (chartType === 'harness') {
      parseHarnessData(sizeData, tbody, wrapper);
    } else if (chartType === 'sand_gaiters') {
      parseSandGaitersData(sizeData, tbody, wrapper);
    }
    
    // Initialize unit toggle
    initUnitToggle();
  }
  
  // Parse men shoes data: EUR,US,Foot Length Range or EUR,US,UK,Foot Length Range
  function parseMenShoesData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];
    
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 3) {
        const eur = parts[0].trim();
        if (/size|eur/i.test(eur)) return;

        const us = parts[1].trim();
        let ukSize, footLength;
        
        // Check if data format is EUR,US,UK,Foot Length (4 columns) or EUR,US,Foot Length (3 columns)
        if (parts.length >= 4) {
          // Format: EUR,US,UK,Foot Length Range
          ukSize = parts[2].trim();
          footLength = parts[3].trim();
        } else {
          // Format: EUR,US,Foot Length Range - calculate UK size
          footLength = parts[2].trim();
          const usNum = parseFloat(us);
          ukSize = isNaN(usNum) ? '' : (usNum - 0.5).toString();
        }
        
        rows.push({
          eur: eur,
          us: us,
          uk: ukSize,
          footLength: footLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      const footLengthParts = row.footLength.split('-');
      const footLengthMin = footLengthParts[0];
      const footLengthMax = footLengthParts.length > 1 ? footLengthParts[1] : footLengthMin;
      
      tr.innerHTML = `
        <td>${row.us}</td>
        <td>${row.eur}</td>
        <td class="size-chart-uk-column">${row.uk}</td>
        <td data-mm-min="${footLengthMin}" data-mm-max="${footLengthMax}">
          <span class="size-chart-foot-length-mm">${row.footLength}</span>
          <span class="size-chart-foot-length-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert MM to INCH for initial display
    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse women shoes data: EUR,US,Foot Length Range or EUR,US,UK,Foot Length Range
  function parseWomenShoesData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];
    
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 3) {
        const eur = parts[0].trim();
        if (/size|eur/i.test(eur)) return;

        const us = parts[1].trim();
        let ukSize, footLength;
        
        // Check if data format is EUR,US,UK,Foot Length (4 columns) or EUR,US,Foot Length (3 columns)
        if (parts.length >= 4) {
          // Format: EUR,US,UK,Foot Length Range
          ukSize = parts[2].trim();
          footLength = parts[3].trim();
        } else {
          // Format: EUR,US,Foot Length Range - calculate UK size
          footLength = parts[2].trim();
          const usNum = parseFloat(us);
          ukSize = isNaN(usNum) ? '' : (usNum - 0.5).toString();
        }
        
        rows.push({
          eur: eur,
          us: us,
          uk: ukSize,
          footLength: footLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      const footLengthParts = row.footLength.split('-');
      const footLengthMin = footLengthParts[0];
      const footLengthMax = footLengthParts.length > 1 ? footLengthParts[1] : footLengthMin;
      
      tr.innerHTML = `
        <td>${row.us}</td>
        <td>${row.eur}</td>
        <td class="size-chart-uk-column">${row.uk}</td>
        <td data-mm-min="${footLengthMin}" data-mm-max="${footLengthMax}">
          <span class="size-chart-foot-length-mm">${row.footLength}</span>
          <span class="size-chart-foot-length-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert MM to INCH for initial display
    updateUnitDisplay(wrapper, 'mm');
  }

  // Parse men shoes W data
  // Format: EUR,US,UK,Foot Length (INCH),Foot Length (MM),Standard Fit Foot Width (MM),Wide Fit Foot Width (MM)
  function parseMenShoesWData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];

    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length < 7) return;

      const eur = parts[0].trim();
      if (/size|eur/i.test(eur)) return;

      const us = parts[1].trim();
      const uk = parts[2].trim();
      const footLengthInches = parts[3].trim();
      const footLengthMm = parts[4].trim();
      const standardWidth = parts[5].trim();
      const wideWidth = parts[6].trim();

      const footInchRange = parseRangeValue(footLengthInches);
      const footMmRange = parseRangeValue(footLengthMm);
      const standardMmRange = parseRangeValue(standardWidth);
      const wideMmRange = parseRangeValue(wideWidth);

      if (!footInchRange || !footMmRange || !standardMmRange || !wideMmRange) return;

      rows.push({
        eur,
        us,
        uk,
        footLengthInches,
        footInchRange,
        footMmRange,
        standardMmRange,
        wideMmRange
      });
    });

    rows.forEach(row => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <td>${row.us}</td>
        <td>${row.eur}</td>
        <td>${row.uk}</td>
        <td class="size-chart-foot-inch-cell" data-inch-min="${row.footInchRange.min}" data-inch-max="${row.footInchRange.max}">${row.footLengthInches}</td>
        <td class="size-chart-foot-metric-cell" data-mm-min="${row.footMmRange.min}" data-mm-max="${row.footMmRange.max}">
          <span class="size-chart-measurement-cm"></span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td class="size-chart-width-cell" data-mm-min="${row.standardMmRange.min}" data-mm-max="${row.standardMmRange.max}">
          <span class="size-chart-measurement-cm"></span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td class="size-chart-width-cell" data-mm-min="${row.wideMmRange.min}" data-mm-max="${row.wideMmRange.max}">
          <span class="size-chart-measurement-cm"></span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
      `;

      tbody.appendChild(tr);
    });

    updateUnitDisplay(wrapper, 'mm');
  }

  // Parse harness data
  // Format: Size,Waist (CM),Thigh (CM)
  function parseHarnessData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];

    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length < 3) return;

      const size = parts[0].trim();
      if (/size/i.test(size)) return;

      const waist = parts[1].trim();
      const thigh = parts[2].trim();

      rows.push({ size, waist, thigh });
    });

    rows.forEach(row => {
      const tr = document.createElement('tr');
      const waistRange = parseRangeValue(row.waist);
      const thighRange = parseRangeValue(row.thigh);
      tr.innerHTML = `
        <td>${row.size}</td>
        <td data-cm-min="${waistRange ? waistRange.min : ''}" data-cm-max="${waistRange ? waistRange.max : ''}">
          <span class="size-chart-circumference-cm">${row.waist}</span>
          <span class="size-chart-circumference-inch" style="display: none;"></span>
        </td>
        <td data-cm-min="${thighRange ? thighRange.min : ''}" data-cm-max="${thighRange ? thighRange.max : ''}">
          <span class="size-chart-circumference-cm">${row.thigh}</span>
          <span class="size-chart-circumference-inch" style="display: none;"></span>
        </td>
      `;

      tbody.appendChild(tr);
    });

    updateUnitDisplay(wrapper, 'cm');
  }

  // Parse sand gaiters data
  // Format: Measurement,S,M,L
  // Example: Shoe Size,EUR 36-39.5,EUR 40-43.5,EUR 44-47
  function parseSandGaitersData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 4) {
        const measurement = parts[0].trim();
        const s = parts[1].trim();
        const m = parts[2].trim();
        const l = parts[3].trim();
        const tr = document.createElement('tr');
        tr.innerHTML = `
          <td>${measurement}</td>
          <td>${s}</td>
          <td>${m}</td>
          <td>${l}</td>
        `;
        tbody.appendChild(tr);
      }
    });
  }

  // Parse shoes data: EUR,US Men,US Women,Foot Length Range
  function parseShoesData(data, tbody, wrapper) {
    const lines = data.trim().split('\n');
    const rows = [];
    
    lines.forEach(line => {
      const parts = line.trim().split(',');
      if (parts.length >= 4) {
        const eur = parts[0].trim();
        const usMen = parts[1].trim();
        const usWomen = parts[2].trim();
        const footLength = parts[3].trim(); // Format: "245-248" or "245"
        
        // Calculate UK size (typically UK = US - 0.5)
        const usMenNum = parseFloat(usMen);
        const ukSize = isNaN(usMenNum) ? '' : (usMenNum - 0.5).toString();
        
        rows.push({
          eur: eur,
          us: usMen,
          uk: ukSize,
          footLength: footLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      const footLengthParts = row.footLength.split('-');
      const footLengthMin = footLengthParts[0];
      const footLengthMax = footLengthParts.length > 1 ? footLengthParts[1] : footLengthMin;
      
      tr.innerHTML = `
        <td>${row.us}</td>
        <td>${row.eur}</td>
        <td class="size-chart-uk-column">${row.uk}</td>
        <td data-mm-min="${footLengthMin}" data-mm-max="${footLengthMax}">
          <span class="size-chart-foot-length-mm">${row.footLength}</span>
          <span class="size-chart-foot-length-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert MM to INCH for initial display
    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse gloves data
  // Supported formats:
  // 1) Category,Measurement Type,S,M,L
  //    Example: Women's,Palm Circumference,165-180,180-198,
  // 2) Category,Size,Palm Circumference,Middle Finger Length
  //    Example: Women's,S,165-180,70
  function parseGlovesData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const categoryData = {};
    const normalizeValue = (val) => (val === '' || val === undefined || val === null) ? '-' : val;

    function ensureCategory(category) {
      if (!categoryData[category]) {
        categoryData[category] = {
          palmCircumference: { s: '-', m: '-', l: '-' },
          middleFingerLength: { s: '-', m: '-', l: '-' }
        };
      }
      return categoryData[category];
    }
    
    // Parse all lines and group by category
    lines.forEach(line => {
      const parts = splitDataLine(line);

      if (parts.length >= 4 && parts.length < 5) {
        // New format: Category,Size,Palm Circumference,Middle Finger Length
        const category = normalizeCategoryLabel(parts[0].trim());
        if (/category/i.test(category)) return;

        const size = parts[1].trim().toLowerCase();
        const palm = normalizeValue(parts[2].trim());
        const middle = normalizeValue(parts[3].trim());

        if (!['s', 'm', 'l'].includes(size)) return;

        const bucket = ensureCategory(category);
        bucket.palmCircumference[size] = palm;
        bucket.middleFingerLength[size] = middle;
        return;
      }

      if (parts.length >= 5) {
        const category = normalizeCategoryLabel(parts[0].trim());
        if (/category/i.test(category)) return;

        const measurementType = parts[1].trim();
        const s = parts[2].trim();
        const m = parts[3].trim();
        const l = parts[4].trim();
        const bucket = ensureCategory(category);
        
        if (measurementType === 'Palm Circumference') {
          bucket.palmCircumference.s = normalizeValue(s);
          bucket.palmCircumference.m = normalizeValue(m);
          bucket.palmCircumference.l = normalizeValue(l);
        } else if (measurementType === 'Middle Finger Length') {
          bucket.middleFingerLength.s = normalizeValue(s);
          bucket.middleFingerLength.m = normalizeValue(m);
          bucket.middleFingerLength.l = normalizeValue(l);
        }
      }
    });
    
    // Define category order
    const categoryOrder = ["Women's", "Men's", "Unisex"];
    const extraCategories = Object.keys(categoryData).filter(category => !categoryOrder.includes(category));
    const renderOrder = categoryOrder.concat(extraCategories);
    
    // Generate table rows for each category
    renderOrder.forEach(category => {
      if (categoryData[category]) {
        const data = categoryData[category];

        const palmS = parseRangeValue(data.palmCircumference.s);
        const palmM = parseRangeValue(data.palmCircumference.m);
        const palmL = parseRangeValue(data.palmCircumference.l);
        const middleS = parseRangeValue(data.middleFingerLength.s);
        const middleM = parseRangeValue(data.middleFingerLength.m);
        const middleL = parseRangeValue(data.middleFingerLength.l);
        
        // Create row for Palm Circumference
        const tr1 = document.createElement('tr');
        tr1.innerHTML = `
          <td rowspan="2">${category.toUpperCase()}</td>
          <td>PALM CIRCUMFERENCE</td>
          <td data-mm-min="${palmS ? palmS.min : ''}" data-mm-max="${palmS ? palmS.max : ''}"><span class="size-chart-measurement-cm">${data.palmCircumference.s}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
          <td data-mm-min="${palmM ? palmM.min : ''}" data-mm-max="${palmM ? palmM.max : ''}"><span class="size-chart-measurement-cm">${data.palmCircumference.m}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
          <td data-mm-min="${palmL ? palmL.min : ''}" data-mm-max="${palmL ? palmL.max : ''}"><span class="size-chart-measurement-cm">${data.palmCircumference.l}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
        `;
        tbody.appendChild(tr1);
        
        // Create row for Middle Finger Length
        const tr2 = document.createElement('tr');
        tr2.innerHTML = `
          <td>MIDDLE FINGER LENGTH</td>
          <td data-mm-min="${middleS ? middleS.min : ''}" data-mm-max="${middleS ? middleS.max : ''}"><span class="size-chart-measurement-cm">${data.middleFingerLength.s}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
          <td data-mm-min="${middleM ? middleM.min : ''}" data-mm-max="${middleM ? middleM.max : ''}"><span class="size-chart-measurement-cm">${data.middleFingerLength.m}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
          <td data-mm-min="${middleL ? middleL.min : ''}" data-mm-max="${middleL ? middleL.max : ''}"><span class="size-chart-measurement-cm">${data.middleFingerLength.l}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
        `;
        tbody.appendChild(tr2);
      }
    });

    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Parse socks data
  // Format: Category,Sock Size,Foot Length Range,EU Shoe Size
  // Example: Unisex,S,240-260,37-39
  function parseSocksData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const categoryData = {};
    
    // Parse all lines and group by category
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 3) {
        let category = 'Unisex';
        let sockSize;
        let footLength;
        let euShoeSize;

        if (parts.length >= 4) {
          category = normalizeCategoryLabel(parts[0].trim());
          if (/category/i.test(category)) return;
          sockSize = parts[1].trim();
          footLength = parts[2].trim();
          euShoeSize = parts[3].trim();
        } else {
          // Fallback format: Size,Foot Length,EU Size
          sockSize = parts[0].trim();
          footLength = parts[1].trim();
          euShoeSize = parts[2].trim();
        }
        
        if (!categoryData[category]) {
          categoryData[category] = [];
        }
        
        categoryData[category].push({
          sockSize: sockSize,
          footLength: footLength || '-',
          euShoeSize: euShoeSize || '-'
        });
      }
    });
    
    // Define category order
    const categoryOrder = ["Unisex", "Men's", "Women's"];
    const extraCategories = Object.keys(categoryData).filter(category => !categoryOrder.includes(category));
    const renderOrder = categoryOrder.concat(extraCategories);
    
    // Generate table rows for each category
    renderOrder.forEach(category => {
      if (categoryData[category]) {
        const rows = categoryData[category];
        
        rows.forEach((row, index) => {
          const footRange = parseRangeValue(row.footLength);
          const tr = document.createElement('tr');
          
          // First row of each category should have rowspan
          if (index === 0) {
            tr.innerHTML = `
              <td rowspan="${rows.length}">${category.toUpperCase()}</td>
              <td>${row.sockSize}</td>
              <td data-mm-min="${footRange ? footRange.min : ''}" data-mm-max="${footRange ? footRange.max : ''}"><span class="size-chart-measurement-cm">${row.footLength}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
              <td>${row.euShoeSize}</td>
            `;
          } else {
            tr.innerHTML = `
              <td>${row.sockSize}</td>
              <td data-mm-min="${footRange ? footRange.min : ''}" data-mm-max="${footRange ? footRange.max : ''}"><span class="size-chart-measurement-cm">${row.footLength}</span><span class="size-chart-measurement-inch" style="display: none;"></span></td>
              <td>${row.euShoeSize}</td>
            `;
          }
          
          tbody.appendChild(tr);
        });
      }
    });

    updateUnitDisplay(wrapper, 'mm');
  }
  
  // Parse vest/waist packs data
  // Format: Size,Circumference (CM)
  // Example: XS,62-69
  function parseCircumferenceData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];
    
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 2) {
        const size = parts[0].trim();
        if (/size/i.test(size)) return;

        const circumference = parts[1].trim(); // Format: "62-69" or "62"
        
        const circumferenceParts = circumference.split('-');
        const cmMin = toNumber(circumferenceParts[0]);
        const cmMax = circumferenceParts.length > 1 ? toNumber(circumferenceParts[1]) : cmMin;

        if (cmMin === null || cmMax === null) return;
        
        rows.push({
          size: size,
          cmMin: cmMin,
          cmMax: cmMax
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      const range = row.cmMax && row.cmMax !== row.cmMin ? `${row.cmMin}-${row.cmMax}` : row.cmMin.toString();
      
      tr.innerHTML = `
        <td>${row.size}</td>
        <td data-cm-min="${row.cmMin}" data-cm-max="${row.cmMax}">
          <span class="size-chart-circumference-cm">${range}</span>
          <span class="size-chart-circumference-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Parse tops data
  // Format: Size,Specification,Length,Chest Circumference,Shoulder Width,Sleeve Length (CM)
  // Example: XXS,160/84A,67.5,108,43.9,61.5
  function parseTopsData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];
    
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 6) {
        const size = parts[0].trim();
        if (/size/i.test(size)) return;

        const specification = parts[1].trim();
        const length = toNumber(parts[2].trim());
        const chestCircumference = toNumber(parts[3].trim());
        const shoulderWidth = toNumber(parts[4].trim());
        const sleeveLength = toNumber(parts[5].trim());

        if (
          length === null ||
          chestCircumference === null ||
          shoulderWidth === null ||
          sleeveLength === null
        ) return;
        
        rows.push({
          size: size,
          specification: specification,
          length: length,
          chestCircumference: chestCircumference,
          shoulderWidth: shoulderWidth,
          sleeveLength: sleeveLength
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${row.size}</td>
        <td>${row.specification}</td>
        <td data-cm-value="${row.length}">
          <span class="size-chart-measurement-cm">${row.length}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.chestCircumference}">
          <span class="size-chart-measurement-cm">${row.chestCircumference}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.shoulderWidth}">
          <span class="size-chart-measurement-cm">${row.shoulderWidth}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.sleeveLength}">
          <span class="size-chart-measurement-cm">${row.sleeveLength}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Parse bottoms data
  // Format: Size,Specification,Waist,Stretched Waist,Thigh,Hip,Length (CM)
  // Example: 30,160/84A,73,82,62.2,100,99.5
  function parseBottomsData(data, tbody, wrapper) {
    const lines = getDataLines(data);
    const rows = [];
    
    lines.forEach(line => {
      const parts = splitDataLine(line);
      if (parts.length >= 7) {
        const size = parts[0].trim();
        if (/size/i.test(size)) return;

        const specification = parts[1].trim();
        const waist = toNumber(parts[2].trim());
        const stretchedWaist = toNumber(parts[3].trim());
        const thigh = toNumber(parts[4].trim());
        const hip = toNumber(parts[5].trim());
        const length = toNumber(parts[6].trim());

        if (
          waist === null ||
          stretchedWaist === null ||
          thigh === null ||
          hip === null ||
          length === null
        ) return;
        
        rows.push({
          size: size,
          specification: specification,
          waist: waist,
          stretchedWaist: stretchedWaist,
          thigh: thigh,
          hip: hip,
          length: length
        });
      }
    });
    
    // Generate table rows
    rows.forEach(row => {
      const tr = document.createElement('tr');
      
      tr.innerHTML = `
        <td>${row.size}</td>
        <td>${row.specification}</td>
        <td data-cm-value="${row.waist}">
          <span class="size-chart-measurement-cm">${row.waist}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.stretchedWaist}">
          <span class="size-chart-measurement-cm">${row.stretchedWaist}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.thigh}">
          <span class="size-chart-measurement-cm">${row.thigh}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.hip}">
          <span class="size-chart-measurement-cm">${row.hip}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
        <td data-cm-value="${row.length}">
          <span class="size-chart-measurement-cm">${row.length}</span>
          <span class="size-chart-measurement-inch" style="display: none;"></span>
        </td>
      `;
      
      tbody.appendChild(tr);
    });
    
    // Convert CM to INCH for initial display
    updateUnitDisplay(wrapper, 'cm');
  }
  
  // Initialize unit toggle functionality
  function initUnitToggle() {
    const unitButtons = document.querySelectorAll('.size-chart-unit-btn');
    
    unitButtons.forEach(btn => {
      btn.addEventListener('click', function() {
        const unit = this.getAttribute('data-unit');
        const wrapper = this.closest('.size-chart-table-wrapper');
        
        // Update active state
        wrapper.querySelectorAll('.size-chart-unit-btn').forEach(b => {
          b.classList.remove('active');
        });
        this.classList.add('active');
        
        // Update display
        updateUnitDisplay(wrapper, unit);
      });
    });
  }
  
  // Update unit display (MM/INCH for all supported charts)
  function updateUnitDisplay(wrapper, unit) {
    const chartType = wrapper.getAttribute('data-chart-type');

    if (chartType === 'men_shoes' || chartType === 'women_shoes') {
      const shoesFootHeader = wrapper.querySelector('.size-chart-shoes-foot-header');
      if (shoesFootHeader) {
        shoesFootHeader.textContent = unit === 'mm' ? 'FOOT LENGTH (MM)' : 'FOOT LENGTH (INCH)';
      }
    }

    if (chartType === 'gloves' || chartType === 'socks') {
      const glovesLabel = wrapper.querySelector('.size-chart-measurement-label');
      const socksHeader = wrapper.querySelector('.size-chart-socks-foot-header');
      if (glovesLabel) glovesLabel.textContent = unit === 'cm' ? 'SIZE (CM)' : (unit === 'mm' ? 'SIZE (MM)' : 'SIZE (INCH)');
      if (socksHeader) socksHeader.textContent = unit === 'mm' ? 'FOOT LENGTH (MM)' : 'FOOT LENGTH (INCH)';

      const mmCells = wrapper.querySelectorAll('td[data-mm-min]');
      mmCells.forEach(cell => {
        // Skip socks cells when chartType is gloves and vice versa by checking span classes
        const mmMin = parseFloat(cell.getAttribute('data-mm-min'));
        const mmMax = parseFloat(cell.getAttribute('data-mm-max'));
        if (Number.isNaN(mmMin) || Number.isNaN(mmMax)) return;

        const mmSpan = cell.querySelector('.size-chart-measurement-cm');
        const inchSpan = cell.querySelector('.size-chart-measurement-inch');
        if (!mmSpan || !inchSpan) return;

        const inchMin = mmMin / 25.4;
        const inchMax = mmMax / 25.4;

        if (unit === 'cm') {
          // Gloves data is stored in MM; display as CM by dividing by 10
          mmSpan.textContent = formatRange(mmMin / 10, mmMax / 10, 1);
          mmSpan.style.display = '';
          inchSpan.style.display = 'none';
        } else if (unit === 'mm') {
          mmSpan.textContent = formatRange(mmMin, mmMax, 0);
          mmSpan.style.display = '';
          inchSpan.style.display = 'none';
        } else {
          inchSpan.textContent = formatRange(inchMin, inchMax, 1);
          mmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      });
      return;
    }

    if (chartType === 'men_shoes_w') {
      const footInchHeader = wrapper.querySelector('.size-chart-foot-inch-header');
      const footMetricHeader = wrapper.querySelector('.size-chart-foot-metric-header');
      const standardHeader = wrapper.querySelector('.size-chart-standard-width-header');
      const wideHeader = wrapper.querySelector('.size-chart-wide-width-header');

      if (footInchHeader) footInchHeader.style.display = unit === 'inch' ? '' : 'none';
      if (footMetricHeader) footMetricHeader.style.display = unit === 'mm' ? '' : 'none';
      if (standardHeader) standardHeader.textContent = unit === 'mm' ? 'STANDARD FIT FOOT WIDTH (MM)' : 'STANDARD FIT FOOT WIDTH (INCH)';
      if (wideHeader) wideHeader.textContent = unit === 'mm' ? 'WIDE FIT FOOT WIDTH (MM)' : 'WIDE FIT FOOT WIDTH (INCH)';

      wrapper.querySelectorAll('.size-chart-foot-inch-cell').forEach(cell => {
        cell.style.display = unit === 'inch' ? '' : 'none';
      });

      wrapper.querySelectorAll('.size-chart-foot-metric-cell').forEach(cell => {
        cell.style.display = unit === 'mm' ? '' : 'none';
      });

      wrapper.querySelectorAll('.size-chart-foot-metric-cell, .size-chart-width-cell').forEach(cell => {
        const mmMin = parseFloat(cell.getAttribute('data-mm-min'));
        const mmMax = parseFloat(cell.getAttribute('data-mm-max'));
        if (Number.isNaN(mmMin) || Number.isNaN(mmMax)) return;

        const mmSpan = cell.querySelector('.size-chart-measurement-cm');
        const inchSpan = cell.querySelector('.size-chart-measurement-inch');
        if (!mmSpan || !inchSpan) return;

        const inchMin = mmMin / 25.4;
        const inchMax = mmMax / 25.4;

        if (unit === 'mm') {
          mmSpan.textContent = formatRange(mmMin, mmMax, 0);
          mmSpan.style.display = '';
          inchSpan.style.display = 'none';
        } else {
          inchSpan.textContent = formatRange(inchMin, inchMax, 1);
          mmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      });
      return;
    }

    if (chartType === 'harness') {
      const waistHeader = wrapper.querySelector('.size-chart-harness-waist-header');
      const thighHeader = wrapper.querySelector('.size-chart-harness-thigh-header');
      if (waistHeader) waistHeader.textContent = unit === 'cm' ? 'WAIST (CM)' : 'WAIST (INCH)';
      if (thighHeader) thighHeader.textContent = unit === 'cm' ? 'THIGH (CM)' : 'THIGH (INCH)';
    }

    if (chartType === 'vest_packs' || chartType === 'waist_packs') {
      const circumferenceHeader = wrapper.querySelector('.size-chart-circumference-header');
      if (circumferenceHeader) {
        const label = chartType === 'waist_packs' ? 'HIP CIRCUMFERENCE' : 'CIRCUMFERENCE BELOW YOUR RIB';
        circumferenceHeader.textContent = `${label} (${unit === 'cm' ? 'CM' : 'INCH'})`;
      }
    }

    if (chartType === 'tops') {
      const lengthHeader = wrapper.querySelector('.size-chart-tops-length-header');
      const chestHeader = wrapper.querySelector('.size-chart-tops-chest-header');
      const shoulderHeader = wrapper.querySelector('.size-chart-tops-shoulder-header');
      const sleeveHeader = wrapper.querySelector('.size-chart-tops-sleeve-header');
      if (lengthHeader) lengthHeader.textContent = unit === 'cm' ? 'LENGTH (CM)' : 'LENGTH (INCH)';
      if (chestHeader) chestHeader.textContent = unit === 'cm' ? 'CHEST CIRCUMFERENCE (CM)' : 'CHEST CIRCUMFERENCE (INCH)';
      if (shoulderHeader) shoulderHeader.textContent = unit === 'cm' ? 'SHOULDER WIDTH (CM)' : 'SHOULDER WIDTH (INCH)';
      if (sleeveHeader) sleeveHeader.textContent = unit === 'cm' ? 'SLEEVE LENGTH (CM)' : 'SLEEVE LENGTH (INCH)';
    }

    if (chartType === 'bottoms') {
      const waistHeader = wrapper.querySelector('.size-chart-bottoms-waist-header');
      const stretchedWaistHeader = wrapper.querySelector('.size-chart-bottoms-stretched-waist-header');
      const thighHeader = wrapper.querySelector('.size-chart-bottoms-thigh-header');
      const hipHeader = wrapper.querySelector('.size-chart-bottoms-hip-header');
      const lengthHeader = wrapper.querySelector('.size-chart-bottoms-length-header');
      if (waistHeader) waistHeader.textContent = unit === 'cm' ? 'WAIST (CM)' : 'WAIST (INCH)';
      if (stretchedWaistHeader) stretchedWaistHeader.textContent = unit === 'cm' ? 'STRETCHED WAIST (CM)' : 'STRETCHED WAIST (INCH)';
      if (thighHeader) thighHeader.textContent = unit === 'cm' ? 'THIGH (CM)' : 'THIGH (INCH)';
      if (hipHeader) hipHeader.textContent = unit === 'cm' ? 'HIP (CM)' : 'HIP (INCH)';
      if (lengthHeader) lengthHeader.textContent = unit === 'cm' ? 'LENGTH (CM)' : 'LENGTH (INCH)';
    }
    
    // Handle men_shoes, women_shoes, and shoes (MM/INCH)
    const footLengthCells = wrapper.querySelectorAll('td[data-mm-min]');
    footLengthCells.forEach(cell => {
      const mmMin = parseFloat(cell.getAttribute('data-mm-min'));
      const mmMax = parseFloat(cell.getAttribute('data-mm-max'));
      
      const mmSpan = cell.querySelector('.size-chart-foot-length-mm');
      const inchSpan = cell.querySelector('.size-chart-foot-length-inch');
      if (!mmSpan || !inchSpan) return;
      
      if (unit === 'mm') {
        const range = mmMax && mmMax !== mmMin ? `${mmMin}-${mmMax}` : mmMin.toString();
        mmSpan.textContent = range;
        mmSpan.style.display = '';
        inchSpan.style.display = 'none';
      } else {
        const inchMin = (mmMin / 25.4).toFixed(1);
        const inchMax = mmMax && mmMax !== mmMin ? (mmMax / 25.4).toFixed(1) : inchMin;
        const range = mmMax && mmMax !== mmMin ? `${inchMin}-${inchMax}` : inchMin;
        inchSpan.textContent = range;
        mmSpan.style.display = 'none';
        inchSpan.style.display = '';
      }
    });
    
    // Handle vest/waist packs and harness (stored in CM, displayed as CM/INCH)
    const circumferenceCells = wrapper.querySelectorAll('td[data-cm-min]');
    circumferenceCells.forEach(cell => {
      const cmMin = parseFloat(cell.getAttribute('data-cm-min'));
      const cmMax = parseFloat(cell.getAttribute('data-cm-max'));
      
      const cmSpan = cell.querySelector('.size-chart-circumference-cm');
      const inchSpan = cell.querySelector('.size-chart-circumference-inch');
      if (!cmSpan) return;
      
      if (unit === 'cm') {
        const range = cmMax && cmMax !== cmMin ? `${cmMin}-${cmMax}` : cmMin.toString();
        cmSpan.textContent = range;
        cmSpan.style.display = '';
        if (inchSpan) inchSpan.style.display = 'none';
      } else {
        const inchMin = (cmMin / 2.54).toFixed(1);
        const inchMax = cmMax && cmMax !== cmMin ? (cmMax / 2.54).toFixed(1) : inchMin;
        const range = cmMax && cmMax !== cmMin ? `${inchMin}-${inchMax}` : inchMin;
        if (inchSpan) {
          inchSpan.textContent = range;
          cmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      }
    });
    
    // Handle tops and bottoms (stored in CM, displayed as CM/INCH)
    const topsMeasurementCells = wrapper.querySelectorAll('td[data-cm-value]');
    topsMeasurementCells.forEach(cell => {
      const cmValue = parseFloat(cell.getAttribute('data-cm-value'));
      
      const cmSpan = cell.querySelector('.size-chart-measurement-cm');
      const inchSpan = cell.querySelector('.size-chart-measurement-inch');
      if (!cmSpan) return;
      
      if (unit === 'cm') {
        cmSpan.textContent = cmValue.toString().replace(/\.0$/, '');
        cmSpan.style.display = '';
        if (inchSpan) inchSpan.style.display = 'none';
      } else {
        const inchValue = (cmValue / 2.54).toFixed(1);
        if (inchSpan) {
          inchSpan.textContent = inchValue;
          cmSpan.style.display = 'none';
          inchSpan.style.display = '';
        }
      }
    });
  }

  // Initialize size chart functionality
  function initSizeChart() {
    const sizeChartContainer = document.querySelector('.size-chart-container');
    if (!sizeChartContainer) return;

    // Accordion toggle functionality
    const sectionHeaders = document.querySelectorAll('.size-section-header');
    
    sectionHeaders.forEach(header => {
      header.addEventListener('click', function(e) {
        e.preventDefault();
        const section = this.closest('.size-section');
        const allSections = document.querySelectorAll('.size-section');
        
        // Toggle current section
        section.classList.toggle('active');
        
        // Optional: Close other sections (uncomment for single-open behavior)
        // allSections.forEach(s => {
        //   if (s !== section) {
        //     s.classList.remove('active'); 
        //   }
        // });
      });
    });

    // Add keyboard accessibility
    sectionHeaders.forEach(header => {
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      
      header.addEventListener('keypress', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.click();
        }
      });
    });

    // Add ARIA attributes for accessibility
    const sections = document.querySelectorAll('.size-section');
    sections.forEach((section, index) => {
      const header = section.querySelector('.size-section-header');
      const content = section.querySelector('.size-section-content');
      
      const headerId = `size-section-header-${index}`;
      const contentId = `size-section-content-${index}`;
      
      header.setAttribute('id', headerId);
      header.setAttribute('aria-controls', contentId);
      
      content.setAttribute('id', contentId);
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', headerId);
      
      // Update aria-expanded based on active state
      updateAriaExpanded(section);
    });

    // Update ARIA expanded state
    function updateAriaExpanded(section) {
      const header = section.querySelector('.size-section-header');
      const isActive = section.classList.contains('active');
      header.setAttribute('aria-expanded', isActive.toString());
    }

    // Observe class changes to update ARIA
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const section = mutation.target;
          updateAriaExpanded(section);
        }
      });
    });

    sections.forEach(section => {
      observer.observe(section, { attributes: true });
    });

    // Smooth scroll to section if hash in URL
    const hash = window.location.hash;
    if (hash) {
      const targetSection = document.querySelector(hash);
      if (targetSection && targetSection.classList.contains('size-section')) {
        setTimeout(() => {
          targetSection.classList.add('active');
          targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }

    // Table responsive enhancement
    enhanceTableResponsiveness();

    // Add print functionality
    // addPrintButton();
  }

  // Enhance table responsiveness
  function enhanceTableResponsiveness() {
    const tables = document.querySelectorAll('.size-table');
    
    tables.forEach(table => {
      // Add touch scroll hint for mobile
      const wrapper = table.closest('.size-table-wrapper');
      if (wrapper && isMobileDevice()) {
        wrapper.style.cursor = 'grab';
        
        let isScrolling = false;
        wrapper.addEventListener('touchstart', () => {
          isScrolling = true;
          wrapper.style.cursor = 'grabbing';
        });
        
        wrapper.addEventListener('touchend', () => {
          isScrolling = false;
          wrapper.style.cursor = 'grab';
        });
      }
    });
  }

  // Check if device is mobile
  function isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Add print functionality
  function addPrintButton() {
    const header = document.querySelector('.size-chart-header');
    if (!header) return;

    const printBtn = document.createElement('button');
    printBtn.className = 'size-chart-print-btn';
    printBtn.innerHTML = '🖨️ Print Size Guide';
    printBtn.style.cssText = `
      margin: 20px auto;
      padding: 12px 24px;
      background: #2c3e50;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      transition: background 0.3s ease;
      display: block;
    `;

    printBtn.addEventListener('mouseenter', () => {
      printBtn.style.background = '#34495e';
    });

    printBtn.addEventListener('mouseleave', () => {
      printBtn.style.background = '#2c3e50';
    });

    printBtn.addEventListener('click', () => {
      // Open all sections before printing
      document.querySelectorAll('.size-section').forEach(section => {
        section.classList.add('active');
      });

      setTimeout(() => {
        window.print();
      }, 300);
    });

    header.appendChild(printBtn);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initSizeChart();
      initModalSizeChart();
    });
  } else {
    initSizeChart();
    initModalSizeChart();
  }

  // Re-initialize for AJAX-loaded content (Shopify theme extensions)
  document.addEventListener('shopify:section:load', function(e) {
    initSizeChart();
    // 延迟初始化 modal，确保 DOM 已完全加载
    setTimeout(initModalSizeChart, 100);
  });
  
  // When block settings change in editor, reinitialize
  document.addEventListener('shopify:block:select', function(e) {
    // If it's a size_chart block, reinitialize
    if (e.detail.blockId) {
      setTimeout(initModalSizeChart, 100);
    }
  });
  
  // Also handle block:deselect in case modal was open
  document.addEventListener('shopify:block:deselect', function(e) {
    if (e.detail.blockId) {
      setTimeout(initModalSizeChart, 100);
    }
  });
  
  // Initialize when modals are opened
  document.addEventListener('click', function(e) {
    // Open modal
    const trigger = e.target.closest('[aria-controls]');
    if (trigger) {
      const targetId = trigger.getAttribute('aria-controls');
      const modal = document.getElementById(targetId);
      if (modal && modal.classList.contains('sc-modal-overlay')) {
        e.preventDefault();
        portalModalToBody(modal);
        modal.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        setTimeout(() => initModalSizeChart(modal), 50);
        return;
      }
    }

    // Close on backdrop click
    if (e.target.classList.contains('sc-modal-backdrop')) {
      closeSizeChartModal(e.target.closest('.sc-modal-overlay'));
      return;
    }

    // Close button
    const closeBtn = e.target.closest('.sc-modal-close');
    if (closeBtn) {
      closeSizeChartModal(closeBtn.closest('.sc-modal-overlay'));
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') {
      const open = document.querySelector('.sc-modal-overlay:not([hidden])');
      if (open) closeSizeChartModal(open);
    }
  });

  function closeSizeChartModal(modal) {
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    const id = modal.id;
    const trigger = document.querySelector('[aria-controls="' + id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    restoreModalFromBody(modal);
  }

  // A transformed or sticky product-info parent creates a containing block for
  // position: fixed children. While open, place the modal at the document root
  // so its overlay always covers the viewport instead of the product form.
  function portalModalToBody(modal) {
    if (modal.parentElement === document.body) return;

    const placeholder = document.createComment('sc-modal-placeholder');
    modal.parentNode.insertBefore(placeholder, modal);
    modal._scModalPlaceholder = placeholder;
    document.body.appendChild(modal);
  }

  function restoreModalFromBody(modal) {
    const placeholder = modal._scModalPlaceholder;
    if (!placeholder || !placeholder.parentNode) return;

    placeholder.parentNode.replaceChild(modal, placeholder);
    modal._scModalPlaceholder = null;
  }
})();
